import React, { useMemo, useRef, useState } from 'react';
import { Button, Card, Col, Form, Input, notification, Row, Select, Space, Typography, Upload } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import {  getListBlocksRequest } from '../../../../services/onlinestore/blockServiceRequest';
import { getListMiniAppsRequest } from '../../../../services/onlinestore/miniAppsServiceRequest';
import { CloseOutlined, ContainerOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import UploadButton from '../../../../components/UploadButton';
import { getListTagsRequest } from '../../../../services/onlinestore/tagServiceRequest';
import { createBlockITemRequest } from '../../../../services/onlinestore/blockItemServiceRequest';
import { AppSelect } from '../../../../constants/appSelect';

const { Text, Title } = Typography;

const CreateBlockItem = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const watchMiniAppId = Form.useWatch('miniapp_id', form);

  // get data blocks
  const { data: dataBlocks } = useQuery({
    queryKey: ['dataBlocks'],
    queryFn: () => getListBlocksRequest({ page: 1, limit: 1000, status: [1] }),
  });

  // convert list block options
  const dataBlocksOption = useMemo(() => {
    return dataBlocks?.data
      ?.filter((item) => item?.status === 1)
      ?.map((item) => {
        return { label: item?.title, value: item?.id };
      });
  }, [dataBlocks]);

  // get data tags
  const { data: dataTags } = useQuery({
    queryKey: ['dataTags'],
    queryFn: () => getListTagsRequest({ page: 1, limit: 1000 }),
  });

  // convert list tag options
  const dataTagsOption = useMemo(() => {
    return dataTags?.data?.map((item) => {
      return { label: item?.title, value: item?.id };
    });
  }, [dataTags]);

  // get data miniapps
  const { data: dataMiniApps } = useQuery({
    queryKey: ['dataMiniApps'],
    queryFn: () => getListMiniAppsRequest({ page: 1, limit: 1000, status: [1] }),
  });

  // convert list miniapps options
  const dataMiniAppsOption = useMemo(() => {
    return dataMiniApps?.data
      ?.filter((item) => item?.status === 1)
      ?.map((item) => {
        return { label: item?.title, value: item?.id };
      });
  }, [dataMiniApps]);

  // handle submit form
  const { mutate: mutateCreateBlockItem, isPending } = useMutation({
    mutationFn: createBlockITemRequest,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: 'Thêm mới item khối thành công!',
        });
        navigate(-1);
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleSubmitForm = (data) => {
    mutateCreateBlockItem(data);
  };

  return (
    <Form
      name="create-block-item"
      form={form}
      layout="vertical"
      className="mb-5"
      onFinish={handleSubmitForm}
    >
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Thêm item khối</Title>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="icon"
            label={
              <Text>
                Upload icon<span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng tải lên icon' }]}
          >
            <UploadButton name="icon-block-item" />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="title"
            label={
              <Text>
                Tên item khối<span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng nhập item khối' }]}
          >
            <Input placeholder="Nhập item khối..." />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="description"
            label={
              <Text>
                Mô tả <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
          >
            <Input placeholder="Nhập mô tả..." />
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item
            name="block_id"
            label={
              <Text>
                Khối <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng chọn khối' }]}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn khối"
              options={dataBlocksOption}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item name="tag_ids" label={<Text>Tag</Text>}>
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              mode="multiple"
              placeholder="Chọn tags"
              options={dataTagsOption}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item
            name="url"
            label={
              <Text>
                Link url <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng nhập đường link' }]}
          >
            <Input placeholder="Nhập link url" />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="miniapp_id" label={<Text>MiniApp</Text>}>
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn MiniApp"
              options={dataMiniAppsOption}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item
            name="display_title"
            label={<Text>Tiêu đề appbar {watchMiniAppId ? <span className="text-red-700">*</span> : ''}</Text>}
            rules={[{ required: watchMiniAppId, message: 'Vui lòng nhập tiêu đề appbar' }]}
          >
            <Input placeholder="Nhập tiêu đề appbar..." />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="order_place"
            label={
              <Text>
                Thứ tự <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng nhập thứ tự' }]}
          >
            <Input placeholder="Nhập thứ tự..." />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="status"
            label={
              <Text>
                Trạng thái <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn trạng thái"
              options={AppSelect.STATUS}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Row justify="end">
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                  Hủy bỏ
                </Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>
                  Lưu lại
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default CreateBlockItem;
